import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { showErrorToast } from '@/lib/api';
import { 
  CheckCircle, 
  FileText, 
  Plus, 
  Trash2, 
  Building2, 
  Calendar,
  Shield,
  ChevronDown,
  Paperclip,
} from 'lucide-react';
import type { AMLFormPublicView, AMLFormPublicUpdate, AMLFormDocument, DocumentType } from '@/types/aml-forms';
import { 
  getAMLFormByToken, 
  updateAMLFormByToken,
  uploadDocumentPublic,
  downloadDocumentPublic,
  deleteDocumentPublic,
} from '@/services/aml-forms';
import { DocumentUpload } from './DocumentUpload';

export function AMLFormPublic() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState<AMLFormPublicView | null>(null);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [documents, setDocuments] = useState<AMLFormDocument[]>([]);
  const [repeatableInstances, setRepeatableInstances] = useState<Record<number, number>>({});
  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);

  useEffect(() => {
    if (!token) {
      navigate('/');
      return;
    }

    loadForm();
  }, [token]);

  const loadForm = async () => {
    if (!token) return;

    setIsLoading(true);
    try {
      const data = await getAMLFormByToken(token);
      setForm(data);
      setFormData(data.form_data || {});
      setDocuments(data.documents || []);
      
      // Initialize repeatable instances count
      if (data.form_structure?.groups) {
        const instances: Record<number, number> = {};
        const groupIds: string[] = [];
        
        data.form_structure.groups.forEach((group, index) => {
          groupIds.push(`group-${index}`);
          if (group.repeatable) {
            // Count existing instances in form_data
            const existingCount = Object.keys(data.form_data || {}).filter(
              key => key.startsWith(`${index}_`)
            ).length / group.fields.length;
            instances[index] = Math.max(1, Math.floor(existingCount));
          }
        });
        setRepeatableInstances(instances);
        // Expand all groups by default
        setExpandedGroups(groupIds);
      }
    } catch (error) {
      showErrorToast(error, 'Failed to load form');
      setTimeout(() => navigate('/'), 3000);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFieldChange = (key: string, value: string | number | boolean) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleAddRepeatableInstance = (groupIndex: number) => {
    setRepeatableInstances((prev) => ({
      ...prev,
      [groupIndex]: (prev[groupIndex] || 1) + 1,
    }));
  };

  const handleRemoveRepeatableInstance = (groupIndex: number) => {
    const currentCount = repeatableInstances[groupIndex] || 1;
    if (currentCount > 1) {
      setRepeatableInstances((prev) => ({
        ...prev,
        [groupIndex]: currentCount - 1,
      }));
      
      // Remove data for the last instance
      if (form?.form_structure?.groups[groupIndex]) {
        const group = form.form_structure.groups[groupIndex];
        const newFormData = { ...formData };
        group.fields.forEach(field => {
          const key = `${groupIndex}_${currentCount - 1}_${field.key}`;
          delete newFormData[key];
        });
        setFormData(newFormData);
      }
    }
  };

  const handleUploadDocument = async (
    file: File, 
    documentType?: DocumentType, 
    description?: string, 
    fieldId?: string
  ) => {
    if (!token) return;
    
    const newDoc = await uploadDocumentPublic(token, file, {
      document_type: documentType,
      description,
      field_id: fieldId,
    });
    setDocuments(prev => [...prev, newDoc]);
  };

  const handleDownloadDocument = async (doc: AMLFormDocument) => {
    if (!token) return;
    
    const blob = await downloadDocumentPublic(token, doc.token);
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = doc.original_filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const handleDeleteDocument = async (doc: AMLFormDocument) => {
    if (!token) return;
    
    await deleteDocumentPublic(token, doc.token);
    setDocuments(prev => prev.filter(d => d.id !== doc.id));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!token) return;

    setIsSubmitting(true);
    try {
      const updateData: AMLFormPublicUpdate = {
        form_data: formData,
        is_completed: true,
      };
      await updateAMLFormByToken(token, updateData);
      toast({
        title: 'Success',
        description: 'Form submitted successfully. Thank you!',
      });
      loadForm(); // Reload to show completed state
    } catch (error) {
      showErrorToast(error, 'Failed to submit form');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!token) return;

    setIsSubmitting(true);
    try {
      const updateData: AMLFormPublicUpdate = {
        form_data: formData,
        is_completed: false,
      };
      await updateAMLFormByToken(token, updateData);
      toast({
        title: 'Success',
        description: 'Progress saved successfully',
      });
    } catch (error) {
      showErrorToast(error, 'Failed to save progress');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get documents count for a field
  const getFieldDocumentCount = (fieldKey: string) => {
    return documents.filter(d => d.field_id === fieldKey).length;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading form...</p>
        </div>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/50">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Form Not Found</CardTitle>
            <CardDescription>
              The form you're looking for could not be found or the link has expired.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (form.is_completed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/50 py-12 px-4">
        <Card className="w-full max-w-lg">
          <CardHeader className="text-center pb-2">
            <div className="flex items-center justify-center mb-4">
              <div className="rounded-full bg-green-100 p-3">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
            </div>
            <CardTitle className="text-2xl">Form Submitted</CardTitle>
            <CardDescription className="text-base mt-2">
              This form has been successfully submitted and is now under review.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg divide-y">
              <div className="flex items-center gap-3 p-3">
                <Building2 className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-xs text-muted-foreground">Company</p>
                  <p className="font-medium">{form.client_name}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3">
                <Shield className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-xs text-muted-foreground">Submitted To</p>
                  <p className="font-medium">{form.firm_name}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3">
                <FileText className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-xs text-muted-foreground">Form</p>
                  <p className="font-medium">{form.name}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3">
                <Calendar className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-xs text-muted-foreground">Submitted On</p>
                  <p className="font-medium">
                    {form.completed_at 
                      ? new Date(form.completed_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : 'N/A'}
                  </p>
                </div>
              </div>
            </div>
            <p className="text-sm text-muted-foreground text-center mt-4">
              If you have any questions, please contact your representative.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header Card */}
        <Card className="mb-6 border-t-4 border-t-primary">
          <CardHeader>
            <div className="flex items-start gap-4">
              <div className="rounded-lg bg-primary/10 p-3">
                <FileText className="w-6 h-6 text-primary" />
              </div>
              <div className="flex-1">
                <CardTitle className="text-2xl mb-1">{form.name}</CardTitle>
                {form.description && (
                  <CardDescription className="text-base">
                    {form.description}
                  </CardDescription>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="flex flex-wrap gap-4 text-sm">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Building2 className="w-4 h-4" />
                <span>For: <strong>{form.client_name}</strong></span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Shield className="w-4 h-4" />
                <span>From: <strong>{form.firm_name}</strong></span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Form Content */}
        <form onSubmit={handleSubmit}>
          {form.form_structure && form.form_structure.groups ? (
            <div className="space-y-4">
              {form.form_structure.groups.map((group, groupIndex) => {
                const isRepeatable = group.repeatable || false;
                const instanceCount = isRepeatable ? (repeatableInstances[groupIndex] || 1) : 1;
                
                return (
                  <Card key={groupIndex} className="overflow-hidden">
                    <div 
                      className="flex items-center justify-between px-6 py-4 bg-muted/50 border-b cursor-pointer"
                      onClick={() => {
                        const groupId = `group-${groupIndex}`;
                        setExpandedGroups(prev => 
                          prev.includes(groupId) 
                            ? prev.filter(id => id !== groupId)
                            : [...prev, groupId]
                        );
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <h3 className="font-semibold text-lg">{group.groupName}</h3>
                        {isRepeatable && (
                          <Badge variant="outline" className="text-xs">
                            {instanceCount} {instanceCount === 1 ? 'entry' : 'entries'}
                          </Badge>
                        )}
                      </div>
                      <ChevronDown 
                        className={`w-5 h-5 text-gray-400 transition-transform ${
                          expandedGroups.includes(`group-${groupIndex}`) ? 'rotate-180' : ''
                        }`}
                      />
                    </div>
                    
                    {expandedGroups.includes(`group-${groupIndex}`) && (
                      <CardContent className="p-0">
                        {Array.from({ length: instanceCount }).map((_, instanceIndex) => (
                          <div 
                            key={instanceIndex} 
                            className={instanceCount > 1 ? "border-b last:border-b-0" : ""}
                          >
                            {instanceCount > 1 && (
                              <div className="flex items-center justify-between px-6 py-2 bg-blue-50 border-b">
                                <span className="text-sm font-medium text-blue-700">
                                  {group.groupName} #{instanceIndex + 1}
                                </span>
                                {instanceIndex === instanceCount - 1 && instanceIndex > 0 && (
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleRemoveRepeatableInstance(groupIndex)}
                                    className="h-7 text-red-600 hover:text-red-700 hover:bg-red-50"
                                  >
                                    <Trash2 className="w-4 h-4 mr-1" />
                                    Remove
                                  </Button>
                                )}
                              </div>
                            )}
                            
                            <Table>
                              <TableBody>
                                {group.fields.map((field) => {
                                  const fieldKey = isRepeatable 
                                    ? `${groupIndex}_${instanceIndex}_${field.key}`
                                    : field.key;
                                  const fieldValue = formData[fieldKey] !== undefined 
                                    ? formData[fieldKey] 
                                    : field.defaultValue ?? '';
                                  const docCount = getFieldDocumentCount(fieldKey);
                                  
                                  return (
                                    <TableRow key={field.key} className="hover:bg-transparent">
                                      <TableCell className="w-1/3 align-top py-4 font-medium text-foreground bg-gray-50/50">
                                        <div className="flex items-start gap-1">
                                          <span>{field.label}</span>
                                          {field.required && (
                                            <span className="text-red-500">*</span>
                                          )}
                                        </div>
                                        {field.type === 'file' && docCount > 0 && (
                                          <Badge variant="secondary" className="mt-1 text-xs">
                                            <Paperclip className="w-3 h-3 mr-1" />
                                            {docCount} file{docCount !== 1 ? 's' : ''}
                                          </Badge>
                                        )}
                                      </TableCell>
                                      <TableCell className="py-4">
                                        {field.type === 'boolean' ? (
                                          <div className="flex items-center gap-3">
                                            <Switch
                                              id={fieldKey}
                                              checked={fieldValue === true || fieldValue === 'true'}
                                              onCheckedChange={(checked) => handleFieldChange(fieldKey, checked)}
                                            />
                                            <span className={`text-sm ${
                                              fieldValue === true || fieldValue === 'true' 
                                                ? 'text-green-600 font-medium' 
                                                : 'text-muted-foreground'
                                            }`}>
                                              {fieldValue === true || fieldValue === 'true' ? 'Yes' : 'No'}
                                            </span>
                                          </div>
                                        ) : field.type === 'text_long' ? (
                                          <Textarea
                                            id={fieldKey}
                                            value={String(fieldValue)}
                                            onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
                                            placeholder={`Enter ${field.label.toLowerCase()}`}
                                            required={field.required}
                                            rows={3}
                                            className="resize-y"
                                          />
                                        ) : field.type === 'number' ? (
                                          <Input
                                            id={fieldKey}
                                            type="number"
                                            value={fieldValue}
                                            onChange={(e) => {
                                              const val = e.target.value;
                                              handleFieldChange(fieldKey, val === '' ? '' : Number(val));
                                            }}
                                            placeholder={`Enter ${field.label.toLowerCase()}`}
                                            required={field.required}
                                            step="any"
                                            className="max-w-xs"
                                          />
                                        ) : field.type === 'file' ? (
                                          <DocumentUpload
                                            documents={documents}
                                            onUpload={handleUploadDocument}
                                            onDownload={handleDownloadDocument}
                                            onDelete={handleDeleteDocument}
                                            fieldId={fieldKey}
                                            suggestedDocumentType={field.documentType}
                                            compact
                                          />
                                        ) : (
                                          <Input
                                            id={fieldKey}
                                            type="text"
                                            value={String(fieldValue)}
                                            onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
                                            placeholder={`Enter ${field.label.toLowerCase()}`}
                                            required={field.required}
                                          />
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </div>
                        ))}
                        
                        {isRepeatable && (
                          <div className="px-6 py-3 bg-muted/50 border-t">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => handleAddRepeatableInstance(groupIndex)}
                              className="w-full"
                            >
                              <Plus className="w-4 h-4 mr-2" />
                              Add Another {group.groupName}
                            </Button>
                          </div>
                        )}
                      </CardContent>
                    )}
                  </Card>
                );
              })}
            </div>
          ) : Object.keys(form.form_data).length === 0 ? (
            <Card>
              <CardContent className="text-center py-12 text-muted-foreground">
                <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No fields defined for this form yet.</p>
              </CardContent>
            </Card>
          ) : (
            // Legacy: Render flat form_data
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableBody>
                    {Object.entries(form.form_data).map(([key, defaultValue]) => {
                      const fieldValue = formData[key] !== undefined ? formData[key] : defaultValue;
                      const isTextarea = typeof fieldValue === 'string' && fieldValue.length > 100;
                      
                      return (
                        <TableRow key={key}>
                          <TableCell className="w-1/3 font-medium text-foreground capitalize bg-gray-50/50">
                            {key.replace(/_/g, ' ')}
                          </TableCell>
                          <TableCell>
                            {isTextarea ? (
                              <Textarea
                                id={key}
                                value={fieldValue}
                                onChange={(e) => handleFieldChange(key, e.target.value)}
                                placeholder={`Enter ${key.replace(/_/g, ' ')}`}
                                rows={3}
                              />
                            ) : (
                              <Input
                                id={key}
                                value={fieldValue}
                                onChange={(e) => handleFieldChange(key, e.target.value)}
                                placeholder={`Enter ${key.replace(/_/g, ' ')}`}
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* General Documents Section */}
          <Card className="mt-6">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Paperclip className="w-5 h-5" />
                Supporting Documents
              </CardTitle>
              <CardDescription>
                Upload any additional documents that support your submission
              </CardDescription>
            </CardHeader>
            <CardContent>
              <DocumentUpload
                documents={documents.filter(d => !d.field_id)}
                onUpload={handleUploadDocument}
                onDownload={handleDownloadDocument}
                onDelete={handleDeleteDocument}
              />
            </CardContent>
          </Card>

          {/* Actions */}
          <Card className="mt-6 bg-muted/50">
            <CardContent className="py-4">
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleSaveDraft}
                  disabled={isSubmitting}
                  className="flex-1"
                >
                  Save Draft
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Form'}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground text-center mt-3">
                By submitting this form, you confirm that all information provided is accurate.
              </p>
            </CardContent>
          </Card>
        </form>
      </div>
    </div>
  );
}
